/**
 * Collapsible Analytics Sidebar
 * Displays analytics items from all RCM services in collapsible sections
 */
import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Shield,
  CheckCircle,
  DollarSign,
  FileText,
  RefreshCw,
  Globe,
  AlertTriangle,
  BarChart3,
  Target,
  TrendingUp,
  Clock,
  Activity,
  Brain,
  PieChart,
  Users,
  Link as LinkIcon,
  Building2,
  User,
  Zap,
  Layers,
  LayoutDashboard,
  CreditCard,
  ShieldAlert,
} from 'lucide-react';

const AnalyticsSidebar = ({ activeService, activeTab, onServiceChange, onTabChange }) => {
  const [expandedSections, setExpandedSections] = useState({
    eligibility: true,
    priorAuth: false,
    charges: false,
    claims: false,
    arPosting: false,
    denial: false,
    remittanceAdvice: false,
    contractManagement: false,
    patientBilling: false,
    ediPlatform: false,
    compliance: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // When a service is selected (e.g. from parent or first child click), keep that section
  // expanded so its analytics tabs stay visible (Remittance, Contract Mgmt, etc.).
  useEffect(() => {
    if (!activeService) return;
    setExpandedSections((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, activeService)) return prev;
      if (prev[activeService]) return prev;
      return { ...prev, [activeService]: true };
    });
  }, [activeService]);

  // Analytics items from Insurance Verification (Eligibility & Benefits)
  const eligibilityItems = [
    {
      id: 'verification-success-rate-analysis',
      name: 'Verification Success Rate',
      icon: BarChart3,
      description: 'Deep dive into verification success rates by dimension',
    },
    {
      id: 'pattern-recognition',
      name: 'Root Cause Analysis',
      icon: Target,
      description: 'Root cause analysis',
    },
    {
      id: 'pre-visit-verification',
      name: 'Pre-Visit Verification',
      icon: Clock,
      description: 'Pre-visit verification performance deep dive',
    },
    {
      id: 'payer-intelligence',
      name: 'Payer Intelligence',
      icon: FileText,
      description: 'Payer performance and contract analysis',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: Activity,
      description: 'Provider verification patterns and performance',
    },
    {
      id: 'service-type-analysis',
      name: 'Service Type Analysis',
      icon: PieChart,
      description: 'Service type verification success rates and revenue',
    },
    {
      id: 'temporal-trends',
      name: 'Temporal Trends',
      icon: TrendingUp,
      description: '12-month trends and seasonal patterns',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: DollarSign,
      description: 'Revenue protected, costs, and ROI analysis',
    },
    {
      id: 'process-efficiency',
      name: 'Process Efficiency',
      icon: Zap,
      description: 'Work queue and processing efficiency',
    },
    {
      id: 'compliance-risk',
      name: 'Compliance & Risk',
      icon: Shield,
      description: 'Compliance metrics and risk analysis',
    },
    {
      id: 'kai-intelligence',
      name: 'Kai Intelligence',
      icon: Brain,
      description: 'Comprehensive AI performance and optimization',
    },
  ];

  // Analytics items from Prior Authorization
  const priorAuthItems = [
    {
      id: 'approval-rate-analysis',
      name: 'Approval Rate Analysis',
      icon: BarChart3,
      description: 'Deep dive into approval rates by dimension',
    },
    {
      id: 'pattern-recognition',
      name: 'Root Cause Analysis',
      icon: Target,
      description: 'Root cause analysis',
    },
    {
      id: 'authorization-management',
      name: 'Authorization Management',
      icon: Clock,
      description: 'Processing performance deep dive',
    },
    {
      id: 'payer-intelligence',
      name: 'Payer Intelligence',
      icon: FileText,
      description: 'Payer performance and contract analysis',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: Users,
      description: 'Provider authorization patterns',
    },
    {
      id: 'cpt-service-line',
      name: 'CPT & Service Line',
      icon: PieChart,
      description: 'CPT code and service line analysis',
    },
    {
      id: 'temporal-trends',
      name: 'Trends & History',
      icon: Activity,
      description: 'Time series and predictive analytics',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: DollarSign,
      description: 'Comprehensive financial analysis',
    },
    {
      id: 'process-efficiency',
      name: 'Work Queue',
      icon: Clock,
      description: 'Work queue and process optimization',
    },
    {
      id: 'compliance-risk',
      name: 'Compliance & Risk',
      icon: Shield,
      description: 'Compliance risk and audit readiness',
    },
    {
      id: 'kai-intelligence',
      name: 'Kai Intelligence',
      icon: Brain,
      description: 'AI autonomous performance and intelligence',
    },
  ];

  // Analytics items from Charge Management (micro-app)
  const chargesItems = [
    {
      id: 'charge-capture-success-rate',
      name: 'Charge Management success rate',
      icon: Target,
      description: 'Multi-dimensional success rate analysis',
    },
    {
      id: 'coding-accuracy',
      name: 'Coding Accuracy',
      icon: FileText,
      description: 'Coding quality and accuracy metrics',
    },
    {
      id: 'denial-patterns',
      name: 'Denial Patterns',
      icon: AlertTriangle,
      description: 'Pattern recognition and root cause analysis',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: Users,
      description: 'Provider performance analytics',
    },
    {
      id: 'payer-intelligence',
      name: 'Payer Intelligence',
      icon: DollarSign,
      description: 'Payer performance and requirements',
    },
    {
      id: 'cpt-service-line',
      name: 'CPT Service Line',
      icon: BarChart3,
      description: 'CPT code and service line analysis',
    },
    {
      id: 'temporal-trends',
      name: 'Temporal Trends',
      icon: TrendingUp,
      description: 'Trend analysis over time',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: DollarSign,
      description: 'Revenue and financial metrics',
    },
    {
      id: 'process-efficiency',
      name: 'Process Efficiency',
      icon: Activity,
      description: 'Workflow and efficiency metrics',
    },
    {
      id: 'compliance-risk',
      name: 'Compliance Risk',
      icon: Shield,
      description: 'Compliance and risk analysis',
    },
    {
      id: 'kai-intelligence',
      name: 'Kai Intelligence',
      icon: Brain,
      description: 'AI contribution and performance',
    },
  ];

  // Analytics items from Claims Processing
  const claimsItems = [
    {
      id: 'clean-claim-rate',
      name: 'Clean Claim Rate',
      icon: CheckCircle,
      description: 'Deep dive into clean claim rates and patterns',
    },
    {
      id: 'first-pass-acceptance',
      name: 'First-Pass Acceptance',
      icon: Target,
      description: 'First-pass acceptance analysis',
    },
    {
      id: 'error-patterns',
      name: 'Error Patterns',
      icon: BarChart3,
      description: 'Error pattern recognition and root cause',
    },
    {
      id: 'submission-performance',
      name: 'Submission Performance',
      icon: TrendingUp,
      description: 'Submission lag and performance analysis',
    },
    {
      id: 'payer-intelligence',
      name: 'Payer Intelligence',
      icon: FileText,
      description: 'Payer performance and contract analysis',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: Users,
      description: 'Provider claim patterns and education',
    },
    {
      id: 'cpt-service-line',
      name: 'CPT & Service Line',
      icon: PieChart,
      description: 'CPT code and service line analysis',
    },
    {
      id: 'temporal-trends',
      name: 'Trends & History',
      icon: Activity,
      description: 'Time series and predictive analytics',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: DollarSign,
      description: 'Comprehensive financial analysis',
    },
    {
      id: 'process-efficiency',
      name: 'Process Efficiency',
      icon: Clock,
      description: 'Work queue and process optimization',
    },
    {
      id: 'compliance-risk',
      name: 'Compliance & Risk',
      icon: Shield,
      description: 'Compliance risk and audit readiness',
    },
    {
      id: 'kai-intelligence',
      name: 'Kai Intelligence',
      icon: Brain,
      description: 'AI autonomous performance and intelligence',
    },
  ];

  // Analytics items from AR Posting
  const arPostingItems = [
    {
      id: 'payment-analysis',
      name: 'Payment Analysis',
      icon: DollarSign,
      description: 'Payment posting patterns and trends',
    },
    {
      id: 'reconciliation-analysis',
      name: 'Reconciliation Analysis',
      icon: RefreshCw,
      description: 'Deposit reconciliation efficiency',
    },
    {
      id: 'adjustment-analysis',
      name: 'Adjustment Analysis',
      icon: FileText,
      description: 'Adjustment patterns and approval workflow',
    },
    {
      id: 'payer-performance',
      name: 'Payer Performance',
      icon: Building2,
      description: 'Payer payment patterns and timeliness',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: User,
      description: 'Provider payment posting patterns',
    },
    {
      id: 'temporal-trends',
      name: 'Temporal Trends',
      icon: TrendingUp,
      description: 'Time-based trends and patterns',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: BarChart3,
      description: 'Financial metrics and ROI',
    },
    {
      id: 'process-efficiency',
      name: 'Process Efficiency',
      icon: Zap,
      description: 'Posting efficiency and automation',
    },
    {
      id: 'variance-analysis',
      name: 'Variance Analysis',
      icon: AlertTriangle,
      description: 'Deposit variance patterns',
    },
    {
      id: 'deposit-matching',
      name: 'Deposit Matching',
      icon: LinkIcon,
      description: 'Deposit-posting matching efficiency',
    },
  ];

  // Analytics items from Denial Management
  const denialItems = [
    {
      id: 'denial-analysis',
      name: 'Denial Analysis',
      icon: BarChart3,
      description: 'Deep dive into denial rates and patterns',
    },
    {
      id: 'pattern-recognition',
      name: 'Root Cause analysis',
      icon: Target,
      description: 'Root cause analysis',
    },
    {
      id: 'appeal-analysis',
      name: 'Appeal Analysis',
      icon: TrendingUp,
      description: 'Appeal success rates and outcomes',
    },
    {
      id: 'payer-intelligence',
      name: 'Payer Intelligence',
      icon: FileText,
      description: 'Payer performance and contract analysis',
    },
    {
      id: 'provider-performance',
      name: 'Provider Performance',
      icon: Users,
      description: 'Provider denial patterns and education',
    },
    {
      id: 'cpt-service-line',
      name: 'CPT & Service Line',
      icon: PieChart,
      description: 'CPT code and service line analysis',
    },
    {
      id: 'temporal-trends',
      name: 'Denial Dynamics & Forecasting',
      icon: Activity,
      description: 'Denial dynamics and forecasting analytics',
    },
    {
      id: 'financial-impact',
      name: 'Financial Impact',
      icon: DollarSign,
      description: 'Comprehensive financial analysis',
    },
    {
      id: 'process-efficiency',
      name: 'Process Efficiency',
      icon: Clock,
      description: 'Work queue and process optimization',
    },
    {
      id: 'compliance-risk',
      name: 'Compliance & Risk',
      icon: Shield,
      description: 'Compliance risk and audit readiness',
    },
    {
      id: 'kai-intelligence',
      name: 'Kai Intelligence',
      icon: Brain,
      description: 'AI autonomous performance and intelligence',
    },
    {
      id: 'team-analytics',
      name: 'Team Analytics',
      icon: Users,
      description: 'Work queue assignments vs goals by team member and Kai',
    },
  ];

  // Analytics items from Remittance Advice (ERA / 835)
  const remittanceAdviceItems = [
    {
      id: 'volume',
      name: 'RA Volume & Trends',
      icon: FileText,
      description: 'Volume and payment trends over time',
    },
    {
      id: 'status',
      name: 'Payment Status',
      icon: PieChart,
      description: 'Remittance by status and amounts',
    },
    {
      id: 'financial',
      name: 'Financial Summary',
      icon: DollarSign,
      description: 'Payments, denials, and amounts',
    },
    {
      id: 'processing',
      name: 'Processing Metrics',
      icon: TrendingUp,
      description: 'Volume, underpayments, and timing',
    },
    {
      id: 'era',
      name: 'ERA Analytics',
      icon: Layers,
      description: 'ERA / 835 analytics by payer, CPT, CARC',
    },
    {
      id: 'payment-velocity',
      name: 'Payment Velocity',
      icon: Clock,
      description: 'Days-to-payment trends and cash forecasting',
    },
    {
      id: 'reversal-rate',
      name: 'Reversal Rate',
      icon: RefreshCw,
      description: 'Recoupment rate by payer (ERA/835)',
    },
    {
      id: 'payer-drift',
      name: 'Payer Drift',
      icon: Activity,
      description: 'Payer behavior change vs baseline (denial/underpayment)',
    },
  ];

  // Analytics items from Contract Management
  const contractItems = [
    {
      id: 'overview',
      name: 'Overview',
      icon: LayoutDashboard,
      description: 'Key metrics and quick links',
    },
    {
      id: 'contract-value',
      name: 'Contract Value',
      icon: FileText,
      description: 'Contract counts and value metrics',
    },
    {
      id: 'payer-mix',
      name: 'Payer Mix',
      icon: Building2,
      description: 'Contracts and volume by payer',
    },
    {
      id: 'compliance',
      name: 'Compliance',
      icon: Shield,
      description: 'Payment accuracy, underpayments, and compliance metrics',
    },
    {
      id: 'team-analytics',
      name: 'Team Analytics',
      icon: Users,
      description: 'Queue actuals vs app-level goals',
    },
  ];

  // Analytics items from Patient Billing
  const patientBillingItems = [
    {
      id: 'ar-aging',
      name: 'A/R Aging',
      icon: Clock,
      description: 'Outstanding balances by aging bucket',
    },
    {
      id: 'collection-performance',
      name: 'Collection Performance',
      icon: TrendingUp,
      description: 'Collection rate, recovery trends, and outcomes',
    },
    {
      id: 'payment-mix',
      name: 'Payment Mix',
      icon: PieChart,
      description: 'Payer vs patient-pay and channel distribution',
    },
    {
      id: 'statement-cycle',
      name: 'Statement Cycle',
      icon: FileText,
      description: 'Statement delivery, response, and follow-up metrics',
    },
    {
      id: 'bad-debt-risk',
      name: 'Bad Debt & Risk',
      icon: AlertTriangle,
      description: 'Delinquency, write-off, and risk indicators',
    },
    {
      id: 'team-analytics',
      name: 'Team Analytics',
      icon: Users,
      description: 'Queue actuals vs app-level goals',
    },
  ];

  // Analytics items from EDI Platform
  const ediPlatformItems = [
    {
      id: 'transaction-volume',
      name: 'Transaction Volume',
      icon: BarChart3,
      description: 'EDI transaction volume and trends',
    },
    {
      id: 'processing-performance',
      name: 'Processing Performance',
      icon: Activity,
      description: 'EDI processing speed and efficiency',
    },
    {
      id: 'error-rates',
      name: 'Error Rates',
      icon: AlertTriangle,
      description: 'EDI error rates and patterns',
    },
    {
      id: 'payer-connectivity',
      name: 'Payer Connectivity',
      icon: Globe,
      description: 'Payer connection status and uptime',
    },
    {
      id: 'compliance-monitoring',
      name: 'Compliance Monitoring',
      icon: Shield,
      description: 'HIPAA and EDI compliance metrics',
    },
  ];

  // Analytics items from RCM Compliance
  const complianceItems = [
    {
      id: 'overview',
      name: 'Compliance Overview',
      icon: ShieldAlert,
      description: 'Checks, findings, overrides, and model posture',
    },
    {
      id: 'findings-by-domain',
      name: 'Findings by Domain',
      icon: PieChart,
      description: 'Finding mix across coding, billing, and documentation',
    },
    {
      id: 'dispositions',
      name: 'Disposition Mix',
      icon: Activity,
      description: 'Open, in review, resolved, and dismissed outcomes',
    },
    {
      id: 'audit-readiness',
      name: 'Audit Readiness',
      icon: Target,
      description: 'Queue pressure and remediation readiness signals',
    },
  ];

  const serviceSections = [
    {
      id: 'eligibility',
      name: 'Eligibility & Benefits',
      icon: Shield,
      items: eligibilityItems,
      color: 'blue',
    },
    {
      id: 'priorAuth',
      name: 'Prior Authorization',
      icon: CheckCircle,
      items: priorAuthItems,
      color: 'green',
    },
    {
      id: 'charges',
      name: 'Charge Management',
      icon: DollarSign,
      items: chargesItems,
      color: 'purple',
    },
    {
      id: 'claims',
      name: 'Claims Processing',
      icon: FileText,
      items: claimsItems,
      color: 'indigo',
    },
    {
      id: 'arPosting',
      name: 'AR Posting',
      icon: RefreshCw,
      items: arPostingItems,
      color: 'teal',
    },
    {
      id: 'denial',
      name: 'Denials & Appeals',
      icon: AlertTriangle,
      items: denialItems,
      color: 'red',
    },
    {
      id: 'remittanceAdvice',
      name: 'Remittance Advice',
      icon: BarChart3,
      items: remittanceAdviceItems,
      color: 'orange',
    },
    {
      id: 'contractManagement',
      name: 'Contract Management',
      icon: LayoutDashboard,
      items: contractItems,
      color: 'teal',
    },
    {
      id: 'patientBilling',
      name: 'Patient Billing',
      icon: CreditCard,
      items: patientBillingItems,
      color: 'purple',
    },
    {
      id: 'compliance',
      name: 'RCM Compliance',
      icon: ShieldAlert,
      items: complianceItems,
      color: 'indigo',
    },
    {
      id: 'ediPlatform',
      name: 'EDI Platform',
      icon: Globe,
      items: ediPlatformItems,
      color: 'orange',
    },
  ];

  const getColorClasses = (color, isActive) => {
    const colorMap = {
      blue: {
        bg: isActive ? 'bg-blue-50' : '',
        text: isActive ? 'text-blue-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-blue-600' : '',
        icon: isActive ? 'text-blue-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-blue-100' : '',
        itemText: isActive ? 'text-blue-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-blue-600' : '',
        itemIcon: isActive ? 'text-blue-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-blue-700' : 'text-gray-500',
      },
      green: {
        bg: isActive ? 'bg-green-50' : '',
        text: isActive ? 'text-green-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-green-600' : '',
        icon: isActive ? 'text-green-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-green-100' : '',
        itemText: isActive ? 'text-green-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-green-600' : '',
        itemIcon: isActive ? 'text-green-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-green-700' : 'text-gray-500',
      },
      purple: {
        bg: isActive ? 'bg-purple-50' : '',
        text: isActive ? 'text-purple-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-purple-600' : '',
        icon: isActive ? 'text-purple-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-purple-100' : '',
        itemText: isActive ? 'text-purple-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-purple-600' : '',
        itemIcon: isActive ? 'text-purple-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-purple-700' : 'text-gray-500',
      },
      indigo: {
        bg: isActive ? 'bg-indigo-50' : '',
        text: isActive ? 'text-indigo-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-indigo-600' : '',
        icon: isActive ? 'text-indigo-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-indigo-100' : '',
        itemText: isActive ? 'text-indigo-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-indigo-600' : '',
        itemIcon: isActive ? 'text-indigo-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-indigo-700' : 'text-gray-500',
      },
      teal: {
        bg: isActive ? 'bg-teal-50' : '',
        text: isActive ? 'text-teal-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-teal-600' : '',
        icon: isActive ? 'text-teal-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-teal-100' : '',
        itemText: isActive ? 'text-teal-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-teal-600' : '',
        itemIcon: isActive ? 'text-teal-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-teal-700' : 'text-gray-500',
      },
      red: {
        bg: isActive ? 'bg-red-50' : '',
        text: isActive ? 'text-red-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-red-600' : '',
        icon: isActive ? 'text-red-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-red-100' : '',
        itemText: isActive ? 'text-red-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-red-600' : '',
        itemIcon: isActive ? 'text-red-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-red-700' : 'text-gray-500',
      },
      orange: {
        bg: isActive ? 'bg-orange-50' : '',
        text: isActive ? 'text-orange-700' : 'text-gray-700',
        border: isActive ? 'border-l-4 border-orange-600' : '',
        icon: isActive ? 'text-orange-600' : 'text-gray-500',
        itemBg: isActive ? 'bg-orange-100' : '',
        itemText: isActive ? 'text-orange-900' : 'text-gray-600',
        itemBorder: isActive ? 'border-l-2 border-orange-600' : '',
        itemIcon: isActive ? 'text-orange-600' : 'text-gray-400',
        itemDesc: isActive ? 'text-orange-700' : 'text-gray-500',
      },
    };
    return colorMap[color] || colorMap.blue;
  };

  const renderSection = (section) => {
    const isExpanded = expandedSections[section.id];
    const isActiveService = activeService === section.id;
    const colors = getColorClasses(section.color, isActiveService);

    return (
      <div key={section.id} className="mb-1">
        {/* Section Header */}
        <button
          onClick={() => toggleSection(section.id)}
          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-md transition-colors hover:bg-gray-50 ${
            colors.bg
          } ${colors.text} ${colors.border}`}
        >
          <div className="flex items-center space-x-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <section.icon className={`w-5 h-5 ${colors.icon}`} />
            <span>{section.name}</span>
          </div>
        </button>

        {/* Section Items */}
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeService === section.id && activeTab === item.id;
              const itemColors = getColorClasses(section.color, isActive);

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onServiceChange(section.id);
                    onTabChange(item.id);
                  }}
                  className={`w-full flex items-start px-3 py-2 text-sm rounded-md transition-colors text-left hover:bg-gray-50 ${
                    itemColors.itemBg
                  } ${itemColors.itemText} ${itemColors.itemBorder}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 mr-2 flex-shrink-0 ${itemColors.itemIcon}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-xs ${itemColors.itemText}`}>
                      {item.name}
                    </div>
                    <div className={`text-xs mt-0.5 ${itemColors.itemDesc}`}>
                      {item.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 text-blue-600 mr-2" />
          Analytics
        </h2>
        <p className="text-xs text-gray-500 mt-1">Cross-service analytics navigation</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        {/* Overview Option */}
        <button
          onClick={() => {
            onServiceChange(null);
            onTabChange(null);
          }}
          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-md transition-colors mb-2 ${
            !activeService
              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center space-x-2">
            <BarChart3 className={`w-5 h-5 ${!activeService ? 'text-blue-600' : 'text-gray-500'}`} />
            <span>Overview Dashboard</span>
          </div>
        </button>
        
        {serviceSections.map((section) => renderSection(section))}
      </nav>
    </div>
  );
};

export default AnalyticsSidebar;

